import { useCallback, useMemo } from 'react';
import _ from 'lodash';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { useFormContext } from 'react-hook-form';

import NgpTextField from './NgpTextField';
import { AUTO_COMPLETE_DEFAULT, TEXT_FIELD_TYPE_TEXT } from '../../utilities/FormUtilities';

const validNumberText = i18n._(t`Value must be numeric`);

function isMinMaxValid(value, bound, testFunc) {
    if (_.isNil(bound) || !value) {
        return true;
    }

    const valueAsNumber = parseFloat(value);
    if (_.isNaN(valueAsNumber)) {
        throw new Error(validNumberText);
    }

    const boundAsNumber = parseFloat(bound);

    return testFunc(valueAsNumber, boundAsNumber);
}

function validateMinMax(value, min, max) {
    try {
        if (!isMinMaxValid(value, min, (test, bound) => test >= bound)) {
            return i18n._(t`Value must be greater than or equal to ${min}`);
        }

        if (!isMinMaxValid(value, max, (test, bound) => test <= bound)) {
            return i18n._(t`Value must be less than or equal to ${max}`);
        }
    } catch (e) {
        return validNumberText;
    }

    return true;
}

export default function NgpNumberField(props) {
    const { inputProps, required = false, min, max, fieldName, allowDecimals, ...rest } = props;
    const { setValue } = useFormContext();

    const replacerRexExp = useMemo(() => (allowDecimals ? /[^0-9.-]/g : /[^[0-9-]/g), [allowDecimals]);

    const numericRules = {
        validate: (value) => validateMinMax(value, min, max),
    };

    const onChangeFunc = useCallback(
        (event) => {
            try {
                const { value: valueRaw } = event.target;
                const strValueRaw = String(valueRaw || '');
                const replacedValueRaw = strValueRaw.replace(replacerRexExp, '');

                if (replacedValueRaw !== strValueRaw) {
                    setValue(fieldName, replacedValueRaw);
                }
            } catch (e) {
                // do nothing, we'll let other validations handle this
            }
        },
        [fieldName, replacerRexExp, setValue]
    );

    return (
        <NgpTextField
            required={required}
            fieldType={TEXT_FIELD_TYPE_TEXT}
            inputProps={{ ...inputProps, autoComplete: AUTO_COMPLETE_DEFAULT }}
            fieldRules={numericRules}
            fieldName={fieldName}
            onChangeFunc={onChangeFunc}
            {...rest}
        />
    );
}
